import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { EditOutlined } from "@ant-design/icons";
import { getApi, postApi, patchApi } from "../../Utlis/apiService";
import {
  GET_ALL_COUPONS,
  GET_COUPON_ASSIGNMENTS,
  GET_ELIGIBLE_USERS,
  ASSIGN_COUPON,
  UNASSIGN_COUPON,
  UPDATE_COUPON_QUANTITY,
} from "../../Utlis/apiPath";
import { errorToast, successToast } from "../../Services/ToastHelper";
import Loader from "../../Components/Loader/Loader";
import { BackArrowIcon } from "../../Components/Icons/BackArrowIcon";
import "./Coupons.scss";

const { Text, Title } = Typography;

const CHIP_STYLES = {
  violet: { background: "#f5f3ff", color: "#6d28d9" },
  slate: { background: "#f1f5f9", color: "#475569" },
  amber: { background: "#fffbeb", color: "#b45309" },
  blue: { background: "#eff6ff", color: "#1d4ed8" },
  emerald: { background: "#ecfdf5", color: "#047857" },
  rose: { background: "#fff1f2", color: "#be123c" },
};

const STATUS_TONE = {
  active: "emerald",
  expired: "rose",
  redeemed: "violet",
  assigned: "blue",
  unused: "slate",
};

const StatChip = ({ tone, children }) => (
  <span
    style={{
      ...(CHIP_STYLES[tone] || CHIP_STYLES.slate),
      display: "inline-block",
      minWidth: 32,
      textAlign: "center",
      fontWeight: 600,
      fontSize: 12.5,
      lineHeight: "20px",
      padding: "2px 10px",
      borderRadius: 6,
    }}
  >
    {children}
  </span>
);

const CouponDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [coupon, setCoupon] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignForm] = Form.useForm();
  const [assignLoading, setAssignLoading] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [eligibleUsersLoading, setEligibleUsersLoading] = useState(false);

  const [quantityModalOpen, setQuantityModalOpen] = useState(false);
  const [quantityForm] = Form.useForm();
  const [quantityLoading, setQuantityLoading] = useState(false);

  const remainingUnassigned = Math.max(
    (coupon?.maxUsageCount ?? 0) - (coupon?.assignedCount ?? 0),
    0
  );

  const selectedUserIds = Form.useWatch("userIds", assignForm) || [];
  const selectableUserCount = Math.min(eligibleUsers.length, remainingUnassigned);
  const allUsersSelected = selectableUserCount > 0 && selectedUserIds.length === selectableUserCount;
  const someUsersSelected = selectedUserIds.length > 0 && !allUsersSelected;

  const handleSelectAllUsers = (e) => {
    const nextIds = e.target.checked
      ? eligibleUsers.slice(0, remainingUnassigned).map((user) => String(user.id))
      : [];
    assignForm.setFieldValue("userIds", nextIds);
    assignForm.validateFields(["userIds"]);
  };

  const fetchCoupon = async () => {
    setIsLoading(true);
    try {
      const response = await getApi(GET_ALL_COUPONS);
      if (response && response.status === 200) {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.coupons ?? response.data?.data ?? [];
        setCoupon(list.find((item) => item.id === id) || null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssignments = async () => {
    setAssignmentsLoading(true);
    try {
      const response = await getApi(`${GET_COUPON_ASSIGNMENTS}/${id}`);
      if (response && response.status === 200) {
        setAssignments(Array.isArray(response.data) ? response.data : []);
      } else {
        setAssignments([]);
        errorToast(response?.message || "Failed to fetch coupon assignments");
      }
    } catch {
      setAssignments([]);
      errorToast("Failed to load coupon assignments");
    } finally {
      setAssignmentsLoading(false);
    }
  };

  const refreshAll = () => {
    fetchCoupon();
    fetchAssignments();
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openAssignModal = async () => {
    setAssignModalOpen(true);
    setEligibleUsersLoading(true);
    try {
      const response = await getApi(GET_ELIGIBLE_USERS);
      if (response && response.status === 200) {
        setEligibleUsers(Array.isArray(response.data) ? response.data : []);
      } else {
        setEligibleUsers([]);
        errorToast(response?.message || "Failed to fetch eligible users");
      }
    } catch {
      setEligibleUsers([]);
      errorToast("Failed to load eligible users");
    } finally {
      setEligibleUsersLoading(false);
    }
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    assignForm.resetFields();
    setEligibleUsers([]);
  };

  const handleAssignCoupon = async (values) => {
    setAssignLoading(true);
    try {
      const expiryDate = values.expiryDate.toISOString();
      const results = await Promise.allSettled(
        values.userIds.map((userId) =>
          postApi(ASSIGN_COUPON, {
            couponId: id,
            userId,
            expiryDate,
            maxUsageCount: values.maxUsageCount,
          })
        )
      );

      const successCount = results.filter(
        (r) => r.status === "fulfilled" && r.value && r.value.status === 200
      ).length;
      const failureMessages = Array.from(
        new Set(
          results
            .filter((r) => !(r.status === "fulfilled" && r.value?.status === 200))
            .map((r) => r.value?.message || r.reason?.message || "Failed to assign coupon")
        )
      );
      const failureCount = results.length - successCount;

      if (successCount > 0) {
        successToast(
          failureCount === 0
            ? `Coupon assigned to ${successCount} user(s) successfully!`
            : `Assigned to ${successCount} user(s); ${failureCount} failed.`
        );
      }
      failureMessages.forEach((message) => errorToast(message));

      if (successCount > 0) {
        closeAssignModal();
        refreshAll();
      }
    } catch {
      errorToast("An error occurred while assigning the coupon.");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleUnassignCoupon = async () => {
    try {
      const response = await postApi(UNASSIGN_COUPON, { couponId: id });
      if (response && response.status === 200) {
        successToast(response.message || "Coupon unassigned successfully!");
        refreshAll();
      } else {
        errorToast(response?.message || "Failed to unassign coupon");
      }
    } catch {
      errorToast("An error occurred while unassigning the coupon.");
    }
  };

  const openQuantityModal = () => {
    quantityForm.setFieldValue("maxUsageCount", coupon?.maxUsageCount ?? 0);
    setQuantityModalOpen(true);
  };

  const closeQuantityModal = () => {
    setQuantityModalOpen(false);
    quantityForm.resetFields();
  };

  const handleUpdateQuantity = async (values) => {
    setQuantityLoading(true);
    try {
      const response = await patchApi(`${UPDATE_COUPON_QUANTITY}/${id}/quantity`, {
        maxUsageCount: values.maxUsageCount,
      });
      if (response && response.status === 200) {
        successToast(response.message || "Max Usage Count updated successfully!");
        closeQuantityModal();
        refreshAll();
      } else {
        errorToast(response?.message || "Failed to update Max Usage Count");
      }
    } catch {
      errorToast("An error occurred while updating Max Usage Count.");
    } finally {
      setQuantityLoading(false);
    }
  };

  return (
    <div className="coupons-page">
      {isLoading && <Loader />}

      <div className="coupons-header">
        <h2
          style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}
          onClick={() => navigate("/coupons")}
        >
          <BackArrowIcon />
          Coupon Details: {coupon?.code || ""}
        </h2>
        <Button type="primary" onClick={openAssignModal} disabled={!coupon}>
          Assign
        </Button>
      </div>

      {coupon && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 24,
            marginBottom: 20,
            padding: "12px 16px",
            background: "#faf9ff",
            border: "1px solid #ede9fe",
            borderRadius: 8,
          }}
        >
          <div>
            <Text type="secondary">Discount</Text>
            <div style={{ marginTop: 4 }}>
              <StatChip tone="violet">
                {coupon.discountType === "PERCENTAGE"
                  ? `${Number(coupon.discountValue)}%`
                  : `₹${Number(coupon.discountValue)}`}
              </StatChip>
            </div>
          </div>
          {/* {coupon.status && (
            <div>
              <Text type="secondary">Status</Text>
              <div style={{ marginTop: 4 }}>
                <StatChip tone={STATUS_TONE[coupon.status?.toLowerCase()] || "slate"}>
                  {coupon.status}
                </StatChip>
              </div>
            </div>
          )} */}
          <div>
            <Text type="secondary">Total Coupons</Text>
            <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
              <StatChip tone="slate">{coupon.maxUsageCount ?? 0}</StatChip>
              <EditOutlined
                onClick={openQuantityModal}
                style={{ fontSize: 12, color: "#6d28d9", cursor: "pointer" }}
              />
            </div>
          </div>
          <div>
            <Text type="secondary">Assigned</Text>
            <div style={{ marginTop: 4 }}><StatChip tone="blue">{coupon.assignedCount ?? 0}</StatChip></div>
          </div>
          <div>
            <Text type="secondary">Unassigned</Text>
            <div style={{ marginTop: 4 }}>
              <StatChip tone="amber">
                {Math.max((coupon.maxUsageCount ?? 0) - (coupon.assignedCount ?? 0), 0)}
              </StatChip>
            </div>
          </div>
          <div>
            <Text type="secondary">Redeemed</Text>
            <div style={{ marginTop: 4 }}><StatChip tone="emerald">{coupon.currentUsageCount ?? 0}</StatChip></div>
          </div>
          <div>
            <Text type="secondary">Unredeemed</Text>
            <div style={{ marginTop: 4 }}>
              <StatChip tone="rose">
                {Math.max((coupon.assignedCount ?? 0) - (coupon.currentUsageCount ?? 0), 0)}
              </StatChip>
            </div>
          </div>
        </div>
      )}

      <Table
        size="small"
        loading={assignmentsLoading}
        dataSource={assignments}
        rowKey="id"
        pagination={false}
        scroll={{ x: "max-content" }}
        columns={[
          {
            title: "S.No",
            key: "slNo",
            width: 60,
            render: (_, __, index) => index + 1,
          },
          {
            title: "Coupon Serial",
            dataIndex: "referenceId",
            key: "referenceId",
            render: (referenceId) =>
              referenceId ? <StatChip tone="slate">{referenceId}</StatChip> : "—",
          },
          {
            title: "Created On",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (createdAt) => (createdAt ? dayjs(createdAt).format("DD/MM/YY") : "—"),
          },
          {
            title: "Assigned to",
            key: "assignedTo",
            render: (_, item) => (
              <div>
                <div>{item.user?.name ?? "—"}</div>
                {item.user?.email && (
                  <Text type="secondary" style={{ fontSize: 12 }}>{item.user.email}</Text>
                )}
              </div>
            ),
          },
          {
            title: "Assigned Date",
            key: "assignedDate",
            render: (_, item) => {
              const assignedDate = item.assignedDate ?? item.createdAt;
              return (
                <StatChip tone="emerald">
                  {assignedDate ? dayjs(assignedDate).format("DD/MM/YY") : "—"}
                </StatChip>
              );
            },
          },
          {
            title: "Expiry Date",
            dataIndex: "expiryDate",
            key: "expiryDate",
            render: (expiryDate) => (
              <StatChip tone="rose">
                {expiryDate ? dayjs(expiryDate).format("DD/MM/YY") : "—"}
              </StatChip>
            ),
          },
          {
            title: "Redeemed Date",
            key: "redeemedDate",
            render: (_, item) => {
              const redeemedDate = item.redeemedDate ?? item.redeemedAt;
              return (
                <StatChip tone="violet">
                  {redeemedDate ? dayjs(redeemedDate).format("DD/MM/YY") : "—"}
                </StatChip>
              );
            },
          },
        ]}
      />

      {/* Assign Coupon Modal */}
      <Modal
        title={`Assign Coupon: ${coupon?.code || ""}`}
        open={assignModalOpen}
        onCancel={closeAssignModal}
        footer={null}
        destroyOnClose
      >
        {coupon?.status === "ASSIGNED" ? (
          <div>
            <p style={{ color: "#374151", marginBottom: 16 }}>
              This coupon is currently assigned to a user.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={closeAssignModal}>Cancel</Button>
              <Popconfirm
                title="Unassign this coupon from the user?"
                onConfirm={async () => {
                  await handleUnassignCoupon();
                  closeAssignModal();
                }}
                okText="Yes"
                cancelText="No"
              >
                <Button danger>Unassign</Button>
              </Popconfirm>
            </div>
          </div>
        ) : (
          <Form form={assignForm} layout="vertical" onFinish={handleAssignCoupon}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>Select Users</span>
              <Checkbox
                checked={allUsersSelected}
                indeterminate={someUsersSelected}
                disabled={eligibleUsersLoading || eligibleUsers.length === 0 || remainingUnassigned === 0}
                onChange={handleSelectAllUsers}
              >
                Select All
              </Checkbox>
            </div>
            <div style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12.5 }}>
                {remainingUnassigned > 0
                  ? `${remainingUnassigned} of ${coupon?.maxUsageCount ?? 0} coupon(s) remaining to assign`
                  : "No coupons remaining — this coupon has reached its Max Usage Count"}
              </Text>
            </div>

            <Form.Item
              name="userIds"
              getValueFromEvent={(value) => {
                if (value.length > remainingUnassigned) {
                  errorToast(
                    `You can only assign up to ${remainingUnassigned} user(s) — this coupon's Max Usage Count is ${coupon?.maxUsageCount ?? 0}`
                  );
                  return value.slice(0, remainingUnassigned);
                }
                return value;
              }}
              rules={[{ required: true, message: "Please select at least one user" }]}
            >
              <Select
                mode="multiple"
                showSearch
                allowClear
                disabled={remainingUnassigned === 0}
                placeholder={eligibleUsersLoading ? "Loading users…" : "Search by name or email"}
                loading={eligibleUsersLoading}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
                notFoundContent={
                  eligibleUsersLoading
                    ? <div style={{ textAlign: "center", padding: "12px 0" }}><Spin size="small" /></div>
                    : <div style={{ textAlign: "center", padding: "12px 0", color: "#9ca3af" }}>No eligible users found</div>
                }
                options={eligibleUsers.map((user) => ({
                  value: String(user.id),
                  label: `${user.firstName} ${user.lastName} ${user.email}`,
                  user,
                }))}
                optionRender={(option) => {
                  const { user } = option.data;
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 0" }}>
                      {user.profilePicture ? (
                        <img
                          src={user.profilePicture}
                          alt=""
                          style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                        />
                      ) : (
                        <div style={{
                          width: 32, height: 32, borderRadius: "50%",
                          background: "linear-gradient(135deg,#6c47ff,#4f46e5)",
                          color: "#fff", display: "flex", alignItems: "center",
                          justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0,
                        }}>
                          {(user.firstName?.[0] || "U").toUpperCase()}
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", lineHeight: 1.3 }}>
                          {user.firstName} {user.lastName}
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.3 }}>
                          {user.email}
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
            </Form.Item>

            <Form.Item
              name="expiryDate"
              label="Expiry Date"
              rules={[{ required: true, message: "Please select an expiry date" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                disabledDate={(current) => current && current < dayjs().startOf("day")}
                showTime
              />
            </Form.Item>

            <Form.Item
              name="maxUsageCount"
              label="Max Usage Count"
              initialValue={1}
              rules={[{ required: true, message: "Please enter the maximum usage count" }]}
            >
              <InputNumber min={1} disabled style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
              <Space>
                <Button onClick={closeAssignModal}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={assignLoading}>
                  Assign
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Update Max Usage Count Modal */}
      <Modal
        title="Update Max Usage Count"
        open={quantityModalOpen}
        onCancel={closeQuantityModal}
        footer={null}
        destroyOnClose
      >
        <Form form={quantityForm} layout="vertical" onFinish={handleUpdateQuantity}>
          <Form.Item
            name="maxUsageCount"
            label="Max Usage Count"
            rules={[
              { required: true, message: "Please enter the max usage count" },
              {
                validator: (_, value) =>
                  value === undefined || value === null || value >= (coupon?.assignedCount ?? 0)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          `Cannot be less than the already assigned count (${coupon?.assignedCount ?? 0})`
                        )
                      ),
              },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={closeQuantityModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={quantityLoading}>
                Update
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CouponDetailsPage;
